/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faProjectDiagram,
  faRefresh,
  faDownload,
  faEdit,
  faSave,
  faTimes,
  faCompress
} from '@fortawesome/free-solid-svg-icons';
import mermaid from 'mermaid';
import panzoom from 'panzoom';
import { saveAs } from 'file-saver';
import {
  generateAgentDiagram,
  getAgentDiagram,
  uploadAgentDiagram,
  DiagramResponse
} from 'services/hvaagent.service';
import HVASpinner from 'components/base/HVASpinner';

interface MermaidDiagramModalProps {
  show: boolean;
  onHide: () => void;
  agentId: string;
  agentName?: string;
  promptType: 'outbound' | 'inbound';
  isDarkMode?: boolean;
}

const MermaidDiagramModal: React.FC<MermaidDiagramModalProps> = ({
  show,
  onHide,
  agentId,
  agentName = 'Agent',
  promptType,
  isDarkMode = false
}) => {
  const [loading, setLoading] = useState(false);
  const [diagram, setDiagram] = useState<DiagramResponse | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [saving, setSaving] = useState(false);

  const mermaidRef = useRef<HTMLDivElement>(null);
  const panzoomInstance = useRef<any>(null);

  // Inicializar Mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: isDarkMode ? 'dark' : 'default',
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis'
      },
      themeVariables: isDarkMode
        ? {
            textColor: '#222222ff'
          }
        : {}
    });
  }, [isDarkMode]);

  // Efecto para re-renderizar cuando cambiamos entre modo edición y visualización
  useEffect(() => {
    if (!editMode && diagram && show) {
      // Pequeño delay para asegurar que el DOM se actualice
      const timer = setTimeout(() => {
        renderDiagram(diagram.diagram);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [editMode, diagram, show]);

  // Cargar diagrama cuando se abre el modal
  useEffect(() => {
    if (show && agentId) {
      loadDiagram();
    }
    return () => {
      // Limpiar panzoom cuando se cierra
      if (panzoomInstance.current) {
        panzoomInstance.current.dispose();
        panzoomInstance.current = null;
      }
    };
  }, [show, agentId, promptType]);

  // Función para cargar el diagrama
  const loadDiagram = async (forceGenerate = false) => {
    setLoading(true);

    try {
      let response: DiagramResponse;

      if (forceGenerate) {
        // Generar nuevo diagrama
        response = await generateAgentDiagram(agentId, {
          force: true,
          type: promptType
        });
      } else {
        // Intentar obtener diagrama existente
        try {
          response = await getAgentDiagram(agentId, promptType);
        } catch (err: any) {
          // Si no existe, generar uno nuevo
          if (err.response?.status === 404) {
            response = await generateAgentDiagram(agentId, {
              type: promptType
            });
          } else {
            throw err;
          }
        }
      }

      setDiagram(response);
      setEditedCode(response.diagram);

      // Renderizar el diagrama
      setTimeout(() => renderDiagram(response.diagram), 100);
    } catch (err: any) {
      console.error('Error loading diagram:', err);
    } finally {
      setLoading(false);
    }
  };

  // Función para renderizar el diagrama con Mermaid
  const renderDiagram = async (code: string) => {
    if (!mermaidRef.current) return;

    try {
      // Limpiar contenido anterior
      mermaidRef.current.innerHTML = '';

      // Limpiar panzoom anterior si existe
      if (panzoomInstance.current) {
        panzoomInstance.current.dispose();
        panzoomInstance.current = null;
      }

      // Crear elemento para el diagrama
      const element = document.createElement('div');
      element.className = 'mermaid';
      element.textContent = code;
      mermaidRef.current.appendChild(element);

      // Renderizar con mermaid
      await mermaid.run();

      // Agregar panzoom después del renderizado
      setTimeout(() => {
        const svgElement = mermaidRef.current?.querySelector('svg');
        if (svgElement) {
          // Ajustar el tamaño del SVG
          svgElement.style.maxWidth = '100%';
          svgElement.style.height = 'auto';

          // Inicializar panzoom
          panzoomInstance.current = panzoom(svgElement, {
            maxZoom: 20,
            minZoom: 0.3,
            initialZoom: 1,
            bounds: true,
            boundsPadding: 0.1
          });
        }
      }, 100);
    } catch (err) {
      console.error('Error rendering diagram:', err);
    }
  };

  // Función para guardar diagrama editado
  const handleSaveEdit = async () => {
    setSaving(true);

    try {
      await uploadAgentDiagram(agentId, {
        mermaidCode: editedCode,
        promptType
      });

      setDiagram(prev =>
        prev
          ? {
              ...prev,
              diagram: editedCode
            }
          : null
      );

      setEditMode(false);
      renderDiagram(editedCode);
    } catch (err: any) {
      console.error('Error saving diagram:', err);
    } finally {
      setSaving(false);
    }
  };

  // Función para exportar diagrama
  const handleExport = () => {
    const svgElement = mermaidRef.current?.querySelector('svg');
    if (!svgElement) return;

    // Clonar el SVG
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Convertir a string
    const svgString = new XMLSerializer().serializeToString(svgClone);

    // Crear blob y descargar
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    saveAs(blob, `${agentName}_${promptType}_diagram.svg`);
  };

  // Función para resetear zoom
  const handleResetZoom = () => {
    if (panzoomInstance.current) {
      panzoomInstance.current.moveTo(0, 0);
      panzoomInstance.current.zoomAbs(0, 0, 1);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faProjectDiagram} className="me-2" />
          {agentName} - {promptType === 'outbound' ? 'Outbound' : 'Inbound'}{' '}
          Flow Diagram
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 bg-body rounded">
        {loading && (
          <div className="text-center fs-9 py-5">
            <HVASpinner />
            <p className="pt-3">Loading diagram...</p>
          </div>
        )}

        {!loading && diagram && !editMode && (
          <div className="position-relative mb-1" style={{ height: '650px' }}>
            {/* Toolbar */}
            <div
              className="position-absolute top-0 end-0 m-3"
              style={{ zIndex: 10 }}
            >
              <ButtonGroup size="sm">
                <Button
                  variant="light"
                  onClick={handleResetZoom}
                  title="Reset Zoom"
                >
                  <FontAwesomeIcon icon={faCompress} />
                </Button>
                <Button
                  variant="light"
                  onClick={handleExport}
                  title="Export as SVG"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
                <Button
                  variant="light"
                  onClick={() => setEditMode(true)}
                  title="Edit Diagram"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
                <Button
                  variant="light"
                  onClick={() => loadDiagram(true)}
                  title="Regenerate Diagram"
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </Button>
              </ButtonGroup>
            </div>

            {/* Info badge */}
            {diagram.cached && (
              <div className="position-absolute top-0 start-0 m-3">
                <small className="text-muted">
                  Cached • Generated:{' '}
                  {new Date(diagram.generated_at!).toLocaleString()}
                </small>
              </div>
            )}

            {/* Diagram container */}
            <div
              ref={mermaidRef}
              className="w-100 h-100 overflow-auto d-flex align-items-center justify-content-center p-4 scrollbar"
            />
          </div>
        )}

        {!loading && diagram && editMode && (
          <div className="p-6 pt-3 pb-3">
            <div className="mb-3">
              <label className="form-label">Edit Mermaid Code:</label>
              <textarea
                className="form-control font-monospace scrollbar"
                rows={30}
                value={editedCode}
                onChange={e => setEditedCode(e.target.value)}
                title="Edit Mermaid diagram code"
                style={{ fontSize: '12px' }}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditMode(false);
                  setEditedCode(diagram.diagram);
                  renderDiagram(diagram.diagram);
                }}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <HVASpinner />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MermaidDiagramModal;
